import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Table, Card, Spinner, Modal, InputGroup } from 'react-bootstrap';
import { Topic } from '../../services/api/types';
import { useTopics } from '../../services/api/hooks/useTopics';
import { getConfig } from '../../services/config';

const Topics = () => {
  const { data: topics, counts, loading: isLoading, createTopic, updateTopic, deleteTopic } = useTopics();
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_sender_name: '',
    default_sender_email: ''
  });
  const [emailPrefix, setEmailPrefix] = useState('');
  const [emailDomain, setEmailDomain] = useState('');

  // Load default domain on mount
  useEffect(() => {
    getConfig('default_sender_email').then(email => {
      if (email) {
        const domain = email.split('@')[1];
        if (domain) {
          setEmailDomain(domain);
          // Set default email for new topics
          setFormData(prev => ({
            ...prev,
            default_sender_email: `prefix@${domain}`
          }));
        }
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Topic name is required.');
      return;
    }

    // Email validation
    if (formData.default_sender_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.default_sender_email)) {
      alert('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingTopic) {
        await updateTopic(editingTopic.id, formData);
      } else {
        await createTopic(formData);
      }
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving topic:', error);
      alert(error.message || 'Failed to save topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (topic: Topic) => {
    setEditingTopic(topic);
    const email = topic.default_sender_email || '';
    const parts = email.split('@');
    setEmailPrefix(parts[0] || '');
    setFormData({
      name: topic.name,
      description: topic.description || '',
      default_sender_name: topic.default_sender_name || '',
      default_sender_email: email || (emailDomain ? `prefix@${emailDomain}` : '')
    });
    setShowModal(true);
  };

  const handleDelete = async (topic: Topic) => {
    if (topic.is_default) {
      alert('Cannot delete the default topic.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete topic "${topic.name}"?`)) {
      try {
        await deleteTopic(topic.id);
      } catch (error: any) {
        console.error('Error deleting topic:', error);
        alert(error.message || 'Failed to delete topic');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTopic(null);
    setEmailPrefix('');
    setFormData({
      name: '',
      description: '',
      default_sender_name: '',
      default_sender_email: ''
    });
  };

  const handleNewTopic = () => {
    setEditingTopic(null);
    setEmailPrefix('');
    setFormData({
      name: '',
      description: '',
      default_sender_name: '',
      default_sender_email: ''
    });
    setShowModal(true);
  };

  return (
    <div className="m-3">
      <Card>
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#d1c4e9' }}>
          <span>Topics</span>
          <Button variant="primary" onClick={handleNewTopic}>
            New Topic
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-info mb-3">
            <strong>Topics:</strong> Organize your email campaigns by topic. Each topic can have default sender information for personalized email delivery.
          </div>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : topics.length === 0 ? (
            <p>No topics found. Create one above!</p>
          ) : (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>ID</th>
                  <th style={{ width: '8%' }}>Name</th>
                  <th style={{ width: '25%' }}>Description</th>
                  <th style={{ width: '22%' }}>Default Sender</th>
                  <th style={{ width: '12%' }}>Subscribers</th>
                  <th style={{ width: '13%' }}>Default</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((topic) => (
                  <tr key={topic.id}>
                    <td><code>{topic.id}</code></td>
                    <td>{topic.name}</td>
                    <td>{topic.description || '-'}</td>
                    <td>
                      {topic.default_sender_name && topic.default_sender_email ? (
                        <>
                          {topic.default_sender_name}
                          <br />
                          <small className="text-muted">{topic.default_sender_email}</small>
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className="badge bg-info">
                        {(counts[topic.id] ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td>
                      {topic.is_default && <span className="badge bg-success">Yes</span>}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEdit(topic)}
                        className="me-1"
                      >
                        Edit
                      </Button>
                      {!topic.is_default && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(topic)}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTopic ? 'Edit Topic' : 'New Topic'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="mb-3">
              <Col>
                <Form.Label>Topic Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={editingTopic?.is_default === true}
                />
                {editingTopic?.is_default === true && (
                  <small className="text-muted d-block mt-2">
                    Cannot rename the default topic
                  </small>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for this topic"
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>Default Sender Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.default_sender_name}
                  onChange={(e) => setFormData({ ...formData, default_sender_name: e.target.value })}
                  placeholder="e.g., John Doe"
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>Default Sender Email</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={emailPrefix}
                    onChange={(e) => {
                      setEmailPrefix(e.target.value);
                      setFormData({ ...formData, default_sender_email: e.target.value ? `${e.target.value}@${emailDomain}` : '' });
                    }}
                    placeholder="prefix"
                  />
                  <InputGroup.Text>@{emailDomain || 'domain.com'}</InputGroup.Text>
                </InputGroup>
                <small className="text-muted">Only the prefix can be changed. Domain is fixed.</small>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Topics;
